/**
 * Web to app handoff for the Pulsomics CTAs.
 *
 * The link strings come from the deeplinks thread: one entry point,
 * `pulsomics_entry`, which the app resolves server-side via
 * `GET /api/v5/pulsomics/entry`. That call is authenticated, so it knows
 * whether the user has been onboarded and whether they have joined a given
 * study, and returns the screens to open in order. Nothing here needs to know
 * who the user is — the same string is correct for everyone.
 *
 * What this file adds is the part a browser cannot do on its own. A custom
 * scheme only resolves if the app is installed: Android Chrome blocks it from a
 * plain anchor, iOS shows an error page, desktop does nothing at all. So a
 * click tries the scheme and, if the page is still here a moment later, sends
 * the visitor to their store instead.
 *
 * When AppsFlyer OneLinks exist for these targets, replace the bodies of
 * `pulsomicsJoinLink` / `pulsomicsStudyLink` with the short links and delete
 * the fallback — OneLink does install-then-deeplink itself, which is both more
 * reliable than a timer and survives the install. The rest of the site already
 * works that way (powerplugs, m1, blood-vision).
 */

export const APP_STORE_URL =
  'https://apps.apple.com/us/app/ultrahuman/id1491286709';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.ultrahuman.android';

/** Opens the Pulsomics hub, onboarding first if the user has never seen it. */
export const pulsomicsJoinLink = () =>
  'ultrahuman://pulsomics_entry?target=join';

/**
 * Opens a single study. `slug` must match the app's identifier — the thread
 * gives `sleep_sufficiency`; the others are unconfirmed, so a study without a
 * slug falls back to the hub rather than opening a broken link.
 */
export const pulsomicsStudyLink = (slug?: string) =>
  slug
    ? `ultrahuman://pulsomics_entry?target=join_study&id=${encodeURIComponent(
        slug,
      )}`
    : pulsomicsJoinLink();

type Platform = 'ios' | 'android' | 'other';

export const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  /* iPadOS 13+ reports itself as a Mac, so a Mac reporting touch is an iPad. */
  const iPadOS =
    /Macintosh/.test(ua) &&
    'maxTouchPoints' in navigator &&
    navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
};

export const storeUrlFor = (platform: Platform) =>
  platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;

/* Long enough that a slow app launch is not mistaken for a missing app, short
   enough that someone without it is not left staring at the page. */
const APP_LAUNCH_GRACE_MS = 1200;

/**
 * Tries to open the app, and sends the visitor to their store if it does not
 * take over.
 *
 * The signal is the page going hidden: when the app opens, the browser is
 * backgrounded and `visibilitychange` fires, so the redirect is cancelled. If
 * the page is still visible when the timer expires, there is no app.
 *
 * Returns true if it attempted the handoff, false on desktop, where there is
 * no app to open and the caller should do something else.
 */
export const openPulsomicsApp = (deeplink: string): boolean => {
  const platform = detectPlatform();
  if (platform === 'other') return false;

  let settled = false;
  const cancel = () => {
    settled = true;
  };

  document.addEventListener('visibilitychange', cancel, { once: true });
  window.addEventListener('pagehide', cancel, { once: true });
  window.addEventListener('blur', cancel, { once: true });

  window.setTimeout(() => {
    document.removeEventListener('visibilitychange', cancel);
    window.removeEventListener('pagehide', cancel);
    window.removeEventListener('blur', cancel);
    if (settled || document.hidden) return;
    window.location.href = storeUrlFor(platform);
  }, APP_LAUNCH_GRACE_MS);

  window.location.href = deeplink;
  return true;
};
